/**
 * RMA processing form — the core disposition card: returned-device
 * identity + KORE activation, issue category, test results, warranty
 * status/fee, additional fees, replacement radio, and comments.
 *
 * Contains:
 * - ProcessingDetailsSection (default export)
 */

import { Box, Button, Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import { Field, useFormikContext } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import IssueCategorySelectComponent from '@sophocles/components/IssueCategorySelectComponent';
import RadioValidationBox from '@sophocles/components/RadioValidationBox/RadioValidationBox';
import TextDisplay from '@sophocles/views/returns/components/TextDisplay';

import { SectionCard } from '../../components/ReturnSection';
import RmaFeeMultiSelect from '../fields/RmaFeeMultiSelect';
import TestResultSelect from '../fields/TestResultSelect';
import WarrantyFeeSelect from '../fields/WarrantyFeeSelect';
import WarrantyStatusSelect from '../fields/WarrantyStatusSelect';

// Filled multiline vertically centers a short value inside a tall slot;
// anchor the textarea to the top so typed text lays out from line 1 down.
const multilineSx = {
  '& .MuiInputBase-root.MuiInputBase-multiline': {
    alignItems: 'flex-start',
  },
  '& .MuiInputBase-inputMultiline': {
    alignSelf: 'flex-start',
    padding: '0 !important',
  },
};

export default function ProcessingDetailsSection({
  rma,
  oldICCID,
  oldActivationState,
  onActivateKore,
  onDeactivateKore,
  issueCategory,
  issueSubCategory,
  onIssueCategoryChange,
  initialTestResultsId,
  onInitialTestResultsChange,
  warrantyStartDate,
  warrantyStatusId,
  onWarrantyStatusChange,
  warrantyFeeId,
  onWarrantyFeeChange,
  selectedRmaFees,
  onRmaFeeAdd,
  onRmaFeeRemove,
  isQuickShip,
  newRadio,
  onNewRadioSelect,
  noRadio,
  onSetNoRadio,
}) {
  const { values, setFieldValue } = useFormikContext();
  const { t } = useTranslation();

  const legacyOldIccid = rma.get('legacy_incoming_iccid', null);
  const legacyNewIccid = rma.get('legacy_new_iccid', null);
  const legacyReplacedIccid = rma.get('legacy_replaced_iccid', null);
  const returnedRadio = rma.get('radio_returned_by_user', null);
  const modelNumber = rma.get('model', t('rma.unknown', 'Unknown'));
  const arrivalFirmwareVersion = rma.get('arrival_firmware_version', t('rma.unknown', 'Unknown'));
  const radioShippedToUserDisplay =
    rma.get('radio_shipped_to_user', null) !== null
      ? rma.getIn(['radio_shipped_to_user', 'iccid'], t('rma.none', 'None'))
      : t('rma.none', 'None');

  return (
    <SectionCard title={t('rma.processingDetails', 'Processing Details')}>
      <Box component="dl" sx={{ mb: 2 }}>
        <TextDisplay title={t('rma.legacyOldIccid', 'Legacy Old ICCID')} show={!!legacyOldIccid}>
          {legacyOldIccid}
        </TextDisplay>
        <TextDisplay title={t('rma.legacyNewIccid', 'Legacy New ICCID')} show={!!legacyNewIccid}>
          {legacyNewIccid}
        </TextDisplay>
        <TextDisplay title={t('rma.legacyReplacementIccid', 'Legacy Replacement ICCID')} show={!!legacyReplacedIccid}>
          {legacyReplacedIccid}
        </TextDisplay>

        <TextDisplay
          title={
            <Stack direction="row" spacing={1}>
              <span>{t('rma.oldIccid', 'Old ICCID')}</span>
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  color:
                    oldActivationState === 'Active'
                      ? 'green'
                      : oldActivationState === 'Deactivated'
                        ? 'red'
                        : oldActivationState === 'Pending'
                          ? 'rgb(255, 152, 0)'
                          : 'inherit',
                }}
              >
                {oldActivationState}
              </span>
            </Stack>
          }
          show={!!oldICCID}
        >
          {returnedRadio !== null ? (
            <Link to={`/support/radios/${returnedRadio.get('id', 0)}`}>{oldICCID}</Link>
          ) : (
            oldICCID
          )}
        </TextDisplay>

        <TextDisplay title={t('rma.modelHash', 'Model #')} show={!!oldICCID}>
          {modelNumber}
        </TextDisplay>
        <TextDisplay title={t('rma.firmwareVersion', 'Firmware Version')} show={!!oldICCID}>
          {arrivalFirmwareVersion}
        </TextDisplay>
        <TextDisplay title={t('rma.quickShipRmaQuestion', 'Quick Ship RMA?')}>
          {isQuickShip ? t('rma.yes', 'Yes') : t('rma.no', 'No')}
        </TextDisplay>
      </Box>

      {oldICCID && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          {oldActivationState === 'Deactivated' && (
            <Button size="small" onClick={onActivateKore}>
              {t('rma.activate', 'Activate')}
            </Button>
          )}
          {oldActivationState === 'Active' && (
            <Button size="small" color="error" onClick={onDeactivateKore}>
              {t('rma.deactivate', 'Deactivate')}
            </Button>
          )}
        </Stack>
      )}

      <Box sx={{ mb: 2 }}>
        <IssueCategorySelectComponent
          onlyShowRmaCategories={true}
          category={issueCategory}
          subCategory={issueSubCategory}
          onChange={onIssueCategoryChange}
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <TestResultSelect value={initialTestResultsId} onChange={onInitialTestResultsChange} />
      </Box>

      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          label={t('rma.unauthorizedSwap', 'Unauthorized Swap')}
          control={
            <Checkbox
              checked={values.unauthorizedSwap}
              onChange={e => setFieldValue('unauthorizedSwap', e.target.checked)}
            />
          }
        />
      </Box>

      {warrantyStartDate && (
        <Box sx={{ mb: 2, fontSize: '14px' }}>
          <Typography variant="body2">
            <b>{t('rma.warrantyStartDate', 'Warranty Start Date')}</b>
          </Typography>
          <Typography variant="body2">{warrantyStartDate}</Typography>
        </Box>
      )}
      {!warrantyStartDate && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2">
            <b>{t('rma.warrantyStartDate', 'Warranty Start Date')}</b>
            {t('rma.couldNotBeDetermined', ': Could not be determined')}
          </Typography>
        </Box>
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <WarrantyStatusSelect value={warrantyStatusId} onChange={onWarrantyStatusChange} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <WarrantyFeeSelect value={warrantyFeeId} onChange={onWarrantyFeeChange} />
        </Box>
      </Stack>

      <Box sx={{ mb: 2 }}>
        <RmaFeeMultiSelect selected={selectedRmaFees} onAdd={onRmaFeeAdd} onRemove={onRmaFeeRemove} />
      </Box>

      {isQuickShip ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2">
            <b>{t('rma.quickShippedRadio', 'Quick Shipped Radio')}</b>
          </Typography>
          <Typography variant="body2">{radioShippedToUserDisplay}</Typography>
        </Box>
      ) : (
        <Box sx={{ mb: 2 }}>
          {noRadio ? (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {t('rma.rmaDevice', 'RMA Device')}
              </Typography>
              <Typography variant="body2">
                {t('rma.noRadioClickPrefix', 'No Radio with this RMA, click')}{' '}
                <Box
                  component="span"
                  sx={{ color: 'primary.main', cursor: 'pointer' }}
                  onClick={() => onSetNoRadio(false)}
                >
                  {t('rma.here', 'here')}
                </Box>{' '}
                {t('rma.toAddOne', 'to add one.')}
              </Typography>
            </Box>
          ) : (
            <Box>
              <RadioValidationBox
                name="new_radio"
                title={t('rma.rmaReplacementDevice', 'RMA Replacement Device')}
                radio={newRadio}
                onRadioSelect={onNewRadioSelect}
              />
              {newRadio === null && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {t('rma.noRadioWithUnit', 'No radio with the unit? Click')}{' '}
                  <Box
                    component="span"
                    sx={{ color: 'primary.main', cursor: 'pointer' }}
                    onClick={() => onSetNoRadio(true)}
                  >
                    {t('rma.here', 'here')}
                  </Box>
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}

      <Box sx={{ mb: 2 }}>
        <Field name="comments">
          {({ field }) => (
            <TextField
              {...field}
              fullWidth
              multiline
              minRows={4}
              label={t('rma.comments', 'Comments')}
              inputProps={{ maxLength: 2048 }}
              sx={multilineSx}
            />
          )}
        </Field>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Field name="hiddenComments">
          {({ field }) => (
            <TextField
              {...field}
              fullWidth
              multiline
              minRows={4}
              label={t('rma.internalCommentsHidden', 'Internal Comments (hidden from customer)')}
              inputProps={{ maxLength: 2048 }}
              sx={multilineSx}
            />
          )}
        </Field>
      </Box>
    </SectionCard>
  );
}
